<script setup>
    import { getKpiDataCounts, getKpiReleaseStats, getKpiRequestsByDay } from '@/shared/helpers/api.ts';
    import { CategoryScale, Chart, Legend, LinearScale, PointElement, Title, Tooltip, BarElement } from 'chart.js';
    import { Bar as BarChart } from 'vue-chartjs';
    import _ from 'lodash';
    import KpiElement from './KpiElement.vue';
    import UsageStatsBackLink from './UsageStatsBackLink.vue';
    import { ref, computed } from 'vue';
    import { http } from '@/shared/httpWrapper';

    Chart.register(
        CategoryScale,
        LinearScale,
        PointElement,
        Title,
        Tooltip,
        Legend,
        BarElement
    );

    const loading = ref(true);
    const dataCounts = ref(null);
    const releaseStats = ref(null);
    const requestsByDay = ref(null);

    const chartData = computed(() => {
        const datasets = [];

        for (const serie in releaseStats.value.series) {
            datasets.push({
                label: serie,
                fill: false,
                data: releaseStats.value.series[serie],
                borderColor: colorForStatus(serie),
                backgroundColor: colorForStatus(serie)
            });
        }
        return {
            labels: releaseStats.value.labels,
            datasets: datasets
        };
    });

    const kpiBoxes = computed(() => {
        if (!dataCounts.value) return null;
        const boxData = [
            {
                heading: 'Scale indicators',
                category: 'scale',
                elements: [
                    {
                        label: 'Library groups',
                        value: dataCounts.value.tagCount
                    },
                    {
                        label: 'Libraries',
                        value: dataCounts.value.libraryCount
                    },
                    {
                        label: 'Library sets',
                        value: dataCounts.value.codeSetCount
                    },
                    {
                        label: 'Library items',
                        value: dataCounts.value.codeCount
                    },
                    {
                        label: 'Item attributes',
                        value: dataCounts.value.codeAttributeCount
                    },
                    {
                        label: 'Attachments',
                        value: dataCounts.value.attachmentCount
                    }
                ]
            },
            {
                heading: 'Usage indicators',
                category: 'usage',
                elements: [
                    {
                        label: 'Access groups',
                        value: dataCounts.value.accessGroupCount
                    },
                    {
                        label: 'Users last week',
                        value: dataCounts.value.usersLastWeek
                    },
                    {
                        label: 'Users last month',
                        value: dataCounts.value.usersLastMonth
                    },
                    {
                        label: 'Users last year',
                        value: dataCounts.value.usersLastYear
                    },
                    {
                        label: 'Users total',
                        value: dataCounts.value.userCount
                    }
                ]
            },
            {
                heading: 'Change & integration indicators',
                category: 'integration',
                elements: [
                    {
                        label: 'Subscriptions',
                        value: dataCounts.value.subscriptionCount
                    },
                    {
                        label: 'Webhooks',
                        value: dataCounts.value.webhookCount
                    },
                    {
                        label: 'Releases',
                        value: dataCounts.value.releaseCount
                    }
                ]
            },
            {
                heading: 'Other indicators',
                elements: [
                    {
                        label: 'Aliases',
                        value: dataCounts.value.aliasCount
                    },
                    {
                        label: 'Scheduled jobs',
                        value: dataCounts.value.scheduleCount
                    }
                ]
            }
        ];

        if (releaseStats.value !== null) {
            const releaseSumsElements = [];
            for (const sum in releaseStats.value.sums) {
                releaseSumsElements.push({
                    label: sum,
                    value: releaseStats.value.sums[sum]
                });
            }
            boxData.push({
                heading: 'Releases by state',
                category: 'integration',
                elements: _.sortBy(releaseSumsElements, x => x.value)
            });
        }

        return boxData;
    });

    async function created() {
        const [kpiDataCount, kpiReleaseStat, kpiRequestsByDay] = await Promise.all([
            await getKpiDataCounts(http),
            await getKpiReleaseStats(http, 60),
            await getKpiRequestsByDay(http)
        ]);
        dataCounts.value = kpiDataCount;
        releaseStats.value = kpiReleaseStat;

        kpiRequestsByDay.datasets[0].backgroundColor = '#a0d4b2';
        requestsByDay.value = kpiRequestsByDay;

        loading.value = false;
    }

    function colorForStatus(status) {
        switch (status) {
            case 'Open':
                return 'rgb(255,215,0)';
            case 'Verifying':
                return 'rgb(255,255,0)';
            case 'Committed':
                return 'rgb(255,0,255)';
            case 'Published':
                return 'rgb(0,255,127)';
            case 'Publishing':
                return 'rgb(255,255,0)';
            case 'Discarded':
                return 'rgb(100,149,237)';
            case 'Planned':
                return 'rgb(255,0,0)';
            case 'Creating':
                return 'rgb(255,0,0)';
            case 'Approved':
                return 'rgb(255,0,0)';
            case 'Committing':
                return 'rgb(255,0,0)';
            default:
                return `rgb(${Math.round(Math.random() * 255)},${Math.round (Math.random () * 255)},${Math.round (Math.random () * 255)})`;
        }
    }

    created();

</script>

<template>
    <div>
        <div style="overflow: auto;">
            <h2 class="title is-3 mb-1">
                Key Performance Indicators
            </h2>
            <usage-stats-back-link />
            <hr>
            <template v-if="dataCounts">
                <div
                    v-for="box in kpiBoxes"
                    :key="box.heading"
                    class="box kpi-box">
                    <div><b>{{ box.heading }}</b></div>
                    <kpi-element
                        v-for="elm in box.elements"
                        :key="elm.label"
                        :label="elm.label"
                        :value="elm.value"
                        :category="box.category" />
                </div>
            </template>
        </div>
        <div class="columns">
            <div class="column">
                <h2 class="title is-3">
                    Requests by day
                </h2>
                <bar-chart
                    v-if="!loading"
                    :data="requestsByDay"
                    style="max-width: 1000px; max-height: 500px;" />
            </div>
            <div class="column">
                <h2 class="title is-3">
                    Release stats
                </h2>
                <h3 class="subtitle">
                    End state of releases - last 60 days
                </h3>
                <bar-chart
                    v-if="!loading"
                    :data="chartData"
                    style="max-width: 1000px; max-height: 500px;" />
            </div>
        </div>
    </div>
</template>

<style scoped>
.kpi-box {
    float: left;
    margin-right: 10px;
}
</style>
